import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, Phone, Globe, MapPin, Users, Save, Lock,
  Eye, EyeOff, CheckCircle, Loader2, Camera, Briefcase, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyProfilePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('company_token');

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');

  // Profile form
  const [form, setForm] = useState({
    company_name: '', phone: '', industry: '', website: '',
    description: '', logo_url: '', location: '', size: '', contact_person: '', contact_email: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/company/auth'); return; }
    fetchProfile();
  }, [token, navigate]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/api/company/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setForm({
        company_name: res.data.company_name || '',
        phone: res.data.phone || '',
        industry: res.data.industry || '',
        website: res.data.website || '',
        description: res.data.description || '',
        logo_url: res.data.logo_url || '',
        location: res.data.location || '',
        size: res.data.size || '',
        contact_person: res.data.contact_person || '',
        contact_email: res.data.contact_email || ''
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/company/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company profile saved');
      const stored = JSON.parse(localStorage.getItem('company_data') || '{}');
      localStorage.setItem('company_data', JSON.stringify({ ...stored, ...form }));
      await fetchProfile();
      window.dispatchEvent(new Event('company-profile-updated'));
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo too large (max 2MB)'); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/company/profile/logo`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setForm((f) => ({ ...f, logo_url: res.data.url }));
      window.dispatchEvent(new Event('company-profile-updated'));
      toast.success('Logo uploaded and saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Logo upload failed'); }
    finally { setUploadingLogo(false); }
  };
  const logoSrc = form.logo_url?.startsWith('/api/') ? `${API}${form.logo_url}` : form.logo_url;

  const changePassword = async () => {
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/api/company/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully!');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPw(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <Loader2 className="animate-spin text-[#002FA7]" size={28} />
    </div>
  );

  const sizeOptions = ['1-10', '11-50', '51-200', '201-500', '500+'];
  const industryOptions = ['Technology', 'Finance', 'Healthcare', 'Education', 'E-commerce', 'Manufacturing', 'Consulting', 'Media', 'Other'];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="company-profile-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/company/dashboard" className="text-[#A1A1AA] hover:text-[#0A0A0A] transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Settings & Company Profile
              </h1>
              <p className="text-xs text-[#A1A1AA]">{profile?.email}</p>
            </div>
          </div>
          {activeSection === 'profile' && (
            <button onClick={saveProfile} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50"
              data-testid="save-profile-btn">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="bg-white border border-[#E4E4E7] rounded overflow-hidden sticky top-6">
              {[
                { id: 'profile', label: 'Company Profile', icon: Building2 },
                { id: 'password', label: 'Password', icon: Lock },
              ].map(item => (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors border-l-2 ${
                    activeSection === item.id
                      ? 'bg-[#002FA7]/5 text-[#002FA7] border-[#002FA7]'
                      : 'text-[#52525B] hover:bg-[#F4F4F5] border-transparent'
                  }`}
                  data-testid={`profile-nav-${item.id}`}>
                  <item.icon size={16} />
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Account Status */}
            <div className="bg-white border border-[#E4E4E7] rounded p-4 mt-4">
              <p className="text-[10px] font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-2">Account Status</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${profile?.email_approved ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                <span className="text-xs text-[#52525B]">{profile?.email_approved ? 'Verified' : 'Pending Verification'}</span>
              </div>
              <p className="text-[10px] text-[#A1A1AA] mt-2">Member since {profile?.created_at?.slice(0, 10)}</p>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <div className="space-y-6" data-testid="profile-form-section">
                {/* Logo & Name */}
                <div className="bg-white border border-[#E4E4E7] rounded p-6">
                  <h2 className="text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-5">Basic Information</h2>
                  <div className="flex items-start gap-6 mb-6">
                    <div className="relative group">
                      {form.logo_url ? (
                        <img src={logoSrc} alt="Company logo" className="w-20 h-20 rounded-lg object-contain border border-[#E4E4E7] bg-white" data-testid="company-logo-preview" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-[#F4F4F5] border border-[#E4E4E7] flex items-center justify-center">
                          <Building2 size={28} className="text-[#A1A1AA]" />
                        </div>
                      )}
                      <label className="mt-2 flex items-center justify-center gap-1 text-[11px] font-semibold text-[#002FA7] cursor-pointer" data-testid="company-logo-upload-label">
                        {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {form.logo_url ? 'Change' : 'Upload'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="company-logo-upload-input" />
                      </label>
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">Company Name</label>
                        <input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                          placeholder="Your company name" data-testid="input-company-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">Logo URL</label>
                        <input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                          placeholder="Upload a logo, or paste https://..." data-testid="input-logo-url" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Users size={12} className="inline mr-1" />Contact Person
                      </label>
                      <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                        placeholder="Full name" data-testid="input-contact-person" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Mail size={12} className="inline mr-1" />Contact Email
                      </label>
                      <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                        placeholder={profile?.email || 'hr@company.com'} data-testid="input-contact-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Phone size={12} className="inline mr-1" />Phone
                      </label>
                      <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                        placeholder="+91 98765 43210" data-testid="input-phone" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Globe size={12} className="inline mr-1" />Website
                      </label>
                      <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                        placeholder="https://yourcompany.com" data-testid="input-website" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <MapPin size={12} className="inline mr-1" />Location
                      </label>
                      <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                        placeholder="City, Country" data-testid="input-location" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Briefcase size={12} className="inline mr-1" />Industry
                      </label>
                      <select value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] bg-white"
                        data-testid="input-industry">
                        <option value="">Select Industry</option>
                        {[...industryOptions, ...(form.industry && !industryOptions.includes(form.industry) ? [form.industry] : [])].map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">
                        <Users size={12} className="inline mr-1" />Company Size
                      </label>
                      <select value={form.size} onChange={e => setForm({ ...form, size: e.target.value })}
                        className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] bg-white"
                        data-testid="input-size">
                        <option value="">Select Size</option>
                        {[...sizeOptions, ...(form.size && !sizeOptions.includes(form.size) ? [form.size] : [])].map(s => <option key={s} value={s}>{s} employees</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white border border-[#E4E4E7] rounded p-6">
                  <h2 className="text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-4">About Your Company</h2>
                  <textarea
                    value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20 min-h-[120px] resize-y"
                    placeholder="Tell candidates about your company, culture, mission..."
                    data-testid="input-description"
                  />
                  <p className="text-[10px] text-[#A1A1AA] mt-2">This description will be visible on your job postings and company page.</p>
                </div>
              </div>
            )}

            {/* Password Section */}
            {activeSection === 'password' && (
              <div className="bg-white border border-[#E4E4E7] rounded p-6" data-testid="password-section">
                <h2 className="text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-5">Change Password</h2>
                <div className="max-w-md space-y-4">
                  {[
                    { key: 'current_password', label: 'Current Password', show: showPw.current, toggle: () => setShowPw(p => ({ ...p, current: !p.current })) },
                    { key: 'new_password', label: 'New Password', show: showPw.new, toggle: () => setShowPw(p => ({ ...p, new: !p.new })) },
                    { key: 'confirm_password', label: 'Confirm New Password', show: showPw.confirm, toggle: () => setShowPw(p => ({ ...p, confirm: !p.confirm })) },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1.5">{field.label}</label>
                      <div className="relative">
                        <input
                          type={field.show ? 'text' : 'password'}
                          value={pwForm[field.key]}
                          onChange={e => setPwForm(p => ({ ...p, [field.key]: e.target.value }))}
                          className="w-full px-3 py-2.5 pr-10 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                          data-testid={`input-${field.key}`}
                        />
                        <button onClick={field.toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#52525B]" type="button">
                          {field.show ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  ))}

                  {pwForm.new_password && pwForm.new_password.length < 6 && (
                    <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />Minimum 6 characters</p>
                  )}
                  {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
                    <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />Passwords do not match</p>
                  )}

                  <button onClick={changePassword} disabled={changingPw || !pwForm.current_password || !pwForm.new_password || pwForm.new_password !== pwForm.confirm_password}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0A0A0A] hover:bg-[#27272A] text-white text-sm font-semibold rounded transition-colors disabled:opacity-40"
                    data-testid="change-password-btn">
                    {changingPw ? <Loader2 className="animate-spin" size={14} /> : <Lock size={14} />}
                    {changingPw ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;
